import { buildApiUrl, fetchJSON } from './crmApi';

export interface TelemarketingMethodologyConfig {
  name: string;
  version: string;
  agent_name: string;
  company_name: string;
  value_proposition?: string;
  enabled_stages: string[];
}

export interface TelemarketingCampaign {
  id: string;
  organization_id: string;
  created_by: string;
  name: string;
  description?: string;
  script_template?: string;
  objective?: string;
  target_criteria?: any;
  call_schedule?: any;
  voice_settings?: any;
  max_attempts?: number;
  retry_delay_hours?: number;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  ai_agent_personality?: string;
  compliance_settings?: any;
  vapi_agent_id?: string | null;
  // Explanatum methodology fields (kept in sync with backend campaignService)
  ai_agent_name?: string;
  company_name?: string;
  value_proposition?: string;
  use_explanatum_methodology?: boolean;
  methodology_config?: TelemarketingMethodologyConfig | null;
  assigned_user_id?: string | null;
  voice_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface TelemarketingCall {
  id: string;
  campaign_id: string;
  target_id: string;
  contact_id?: string;
  call_sid?: string;
  phone_number: string;
  direction: 'outbound' | 'inbound';
  status: string;
  duration?: number;
  start_time?: string;
  end_time?: string;
  recording_url?: string;
  transcript?: string;
  sentiment_score?: number;
  outcome?: string;
  outcome_details?: any;
  lead_score?: number;
  ai_insights?: any;
  objections_encountered?: any;
  next_action?: string;
  crm_activity_id?: string;
  crm_note_id?: string;
  cost_usd?: number;
  created_at: string;
  updated_at: string;
}

export interface CampaignStats {
  total_targets: number;
  pending_calls: number;
  completed_calls: number;
  interested_count: number;
  meetings_booked: number;
  qualified_leads: number;
  avg_sentiment: number;
  total_cost: number;
  conversion_rate: number;
}

export interface CreateCampaignInput {
  organization_id: string;
  name: string;
  description?: string;
  script_template?: string;
  objective?: string;
  target_criteria?: any;
  call_schedule?: any;
  voice_settings?: any;
  max_attempts?: number;
  retry_delay_hours?: number;
  ai_agent_personality?: string;
  compliance_settings?: any;
  status?: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  ai_agent_name?: string;
  company_name?: string;
  value_proposition?: string;
  use_explanatum_methodology?: boolean;
  methodology_config?: TelemarketingMethodologyConfig | null;
  assigned_user_id?: string | null;
  voice_id?: string | null;
}

export interface InitiateCallParams {
  campaign_id: string;
  target_id: string;
  contact_id?: string;
  phone_number: string;
  voice_settings?: any;
}

// ===== CAMPAIGN OPERATIONS =====

export async function listCampaigns(
  organizationId: string,
  filters?: {
    status?: string;
    limit?: number;
    offset?: number;
  }
) {
  const query: Record<string, string> = {
    organization_id: organizationId
  };
  if (filters?.status) query.status = filters.status;
  if (filters?.limit) query.limit = filters.limit.toString();
  if (filters?.offset) query.offset = filters.offset.toString();

  return await fetchJSON<any>(buildApiUrl('/api/telemarketing/campaigns', query));
}

export async function getCampaign(id: string, organizationId: string) {
  const result = await fetchJSON<{ data: TelemarketingCampaign }>(
    buildApiUrl(`/api/telemarketing/campaigns/${id}`, { organization_id: organizationId })
  );
  return result.data;
}

export async function createCampaign(data: CreateCampaignInput) {
  const result = await fetchJSON<{ data: TelemarketingCampaign }>(
    buildApiUrl('/api/telemarketing/campaigns'),
    { method: 'POST', body: data }
  );
  return result.data;
}

export async function updateCampaign(id: string, data: Partial<CreateCampaignInput>) {
  const result = await fetchJSON<{ data: TelemarketingCampaign }>(
    buildApiUrl(`/api/telemarketing/campaigns/${id}`),
    { method: 'PUT', body: data }
  );
  return result.data;
}

export async function deleteCampaign(id: string, organizationId: string) {
  return await fetchJSON(
    buildApiUrl(`/api/telemarketing/campaigns/${id}`, { organization_id: organizationId }),
    { method: 'DELETE' }
  );
}

export async function startCampaign(id: string, organizationId: string) {
  const result = await fetchJSON<{ data: TelemarketingCampaign }>(
    buildApiUrl(`/api/telemarketing/campaigns/${id}/start`),
    { method: 'POST', body: { organization_id: organizationId } }
  );
  return result.data;
}

export async function pauseCampaign(id: string, organizationId: string) {
  const result = await fetchJSON<{ data: TelemarketingCampaign }>(
    buildApiUrl(`/api/telemarketing/campaigns/${id}/pause`),
    { method: 'POST', body: { organization_id: organizationId } }
  );
  return result.data;
}

export async function getCampaignStats(id: string) {
  const result = await fetchJSON<{ data: CampaignStats }>(
    buildApiUrl(`/api/telemarketing/campaigns/${id}/stats`)
  );
  return result.data;
}

export async function getCampaignAnalytics(id: string) {
  const result = await fetchJSON<{ data: any }>(
    buildApiUrl(`/api/telemarketing/campaigns/${id}/analytics`)
  );
  return result.data;
}

// ===== CALL OPERATIONS =====

export async function listCalls(
  campaignId: string,
  filters?: {
    outcome?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }
) {
  const query: Record<string, string> = {};
  if (filters?.outcome) query.outcome = filters.outcome;
  if (filters?.status) query.status = filters.status;
  if (filters?.limit) query.limit = filters.limit.toString();
  if (filters?.offset) query.offset = filters.offset.toString();

  return await fetchJSON<any>(
    buildApiUrl(`/api/telemarketing/campaigns/${campaignId}/calls`, query)
  );
}

export async function getCall(id: string) {
  const result = await fetchJSON<{ data: TelemarketingCall }>(
    buildApiUrl(`/api/telemarketing/calls/${id}`)
  );
  return result.data;
}

export async function initiateCall(params: InitiateCallParams) {
  return await fetchJSON(
    buildApiUrl('/api/telemarketing/calls/initiate'),
    { method: 'POST', body: params }
  );
}

export async function getActiveCalls(campaignId?: string) {
  const query = campaignId ? { campaign_id: campaignId } : undefined;
  const result = await fetchJSON<{ data: TelemarketingCall[] }>(
    buildApiUrl('/api/telemarketing/calls/active', query)
  );
  return result.data;
}

export async function syncCallToCRM(callId: string, organizationId: string) {
  const result = await fetchJSON<{ data: any }>(
    buildApiUrl(`/api/telemarketing/calls/${callId}/sync-crm`),
    { method: 'POST', body: { organization_id: organizationId } }
  );
  return result.data;
}

export async function transferCall(callSid: string, agentPhoneNumber: string) {
  return await fetchJSON(
    buildApiUrl(`/api/telemarketing/calls/${callSid}/transfer`),
    { method: 'POST', body: { agent_phone_number: agentPhoneNumber } }
  );
}

export async function hangupCall(callSid: string) {
  return await fetchJSON(
    buildApiUrl(`/api/telemarketing/calls/${callSid}/hangup`),
    { method: 'POST' }
  );
}

export interface MinutesUsage {
  available: boolean;
  remaining: number;
  limit: number;
  status: string;
}

export async function getMinutesUsage(organizationId: string) {
  const result = await fetchJSON<{ data: MinutesUsage }>(
    buildApiUrl('/api/telemarketing/usage/minutes', { organization_id: organizationId })
  );
  return result.data;
}

export interface PhoneNumberStatus {
  phoneNumber: string | null;
  hasNumber: boolean;
  alreadyProvisioned?: boolean;
  vapiPhoneId?: string;
  twilioSid?: string;
  capabilities?: Record<string, any>;
  countryCode?: string;
  numberType?: string;
  areaCode?: string;
  city?: string;
  monthlyCost?: number;
  purchasedAt?: string;
}

export async function getOrganizationPhoneNumber(organizationId: string) {
  const result = await fetchJSON<{ data: PhoneNumberStatus }>(
    buildApiUrl('/api/telemarketing/phone-number', { organization_id: organizationId })
  );
  return result.data;
}

export interface ProvisionPhoneNumberInput {
  organization_id: string;
  country_code?: string;
  number_type?: 'local' | 'toll_free';
  area_code?: string;
  city?: string;
  address?: {
    customerName: string;
    street: string;
    city: string;
    region?: string;
    postalCode: string;
    countryCode: string;
  };
}

export async function provisionPhoneNumber(input: ProvisionPhoneNumberInput) {
  const result = await fetchJSON<{ data: PhoneNumberStatus }>(
    buildApiUrl('/api/telemarketing/phone-number/provision'),
    { method: 'POST', body: input }
  );
  return result.data;
}

// ===== TEAM MEMBERS =====

export interface TeamMember {
  id: string;
  user_id: string;
  email: string;
  full_name?: string;
  role: string;
  avatar_url?: string;
}

export async function getOrganizationMembers(organizationId: string): Promise<TeamMember[]> {
  const result = await fetchJSON<{ data: TeamMember[] }>(
    buildApiUrl(`/api/telemarketing/team-members?organization_id=${organizationId}`)
  );
  return result.data || [];
}

// ===== CONFIG STATUS =====

export interface TelemarketingConfigStatus {
  ready: boolean;
  aiConfigured: boolean;
  aiError?: string;
  twilioConfigured: boolean;
  twilioError?: string;
  ttsConfigured: boolean;
  ttsError?: string;
  errors: string[];
  warnings: string[];
}

export async function getConfigStatus(): Promise<TelemarketingConfigStatus> {
  const result = await fetchJSON<{ data: TelemarketingConfigStatus }>(
    buildApiUrl('/api/telemarketing/config-status')
  );
  return result.data;
}

// ===== ERROR CODE MAPPING =====

export const ERROR_MESSAGES: Record<string, string> = {
  'TELEMARKETING_AI_CONFIG_MISSING': 'AI service is not configured. Please contact your administrator to set up the telemarketing AI.',
  'TELEMARKETING_TWILIO_CONFIG_MISSING': 'Calling service (Twilio) is not configured. Please contact your administrator to connect Twilio.',
  'TELEMARKETING_TTS_CONFIG_MISSING': 'Voice synthesis is not configured. Please contact your administrator.',
  'TELEMARKETING_NO_PHONE_NUMBER': 'No phone number available. Please provision a number in Settings → Telemarketing.',
  'TELEMARKETING_NO_MINUTES_REMAINING': 'No call minutes remaining. Please purchase additional minutes to continue.',
  'TELEMARKETING_CAMPAIGN_NOT_FOUND': 'Campaign not found. It may have been deleted.',
  'TELEMARKETING_TARGET_NOT_FOUND': 'Call target not found. It may have been removed from the campaign.'
};

export function getErrorMessage(error: any): string {
  // Check if error has a code we recognize
  if (error?.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  // Check if error message contains a known code
  const message = error?.message || error?.toString() || '';
  for (const [code, friendlyMessage] of Object.entries(ERROR_MESSAGES)) {
    if (message.includes(code)) {
      return friendlyMessage;
    }
  }
  // Return original message or generic fallback
  return message || 'An unexpected error occurred. Please try again.';
}

// ===== CAMPAIGN TARGETS =====

export interface CampaignTarget {
  id: string;
  campaign_id: string;
  contact_id?: string;
  phone_number: string;
  contact_name?: string;
  company_name?: string;
  custom_data?: any;
  call_status: string;
  attempts: number;
  outcome?: string;
  priority: number;
  created_at: string;
}

export interface ImportContact {
  name: string;
  phone: string;
  company?: string;
  email?: string;
}

export interface CSVImportResult {
  success: boolean;
  totalRows: number;
  validContacts: number;
  duplicates: number;
  invalidPhones: number;
  dncMatches: number;
  errors: Array<{ row: number; error: string }>;
}

export async function getCampaignTargets(campaignId: string, organizationId: string) {
  const result = await fetchJSON<{ data: CampaignTarget[]; total: number }>(
    buildApiUrl(`/api/telemarketing/campaigns/${campaignId}/targets`, { organization_id: organizationId })
  );
  return result;
}

export async function addCampaignTargets(
  campaignId: string, 
  organizationId: string, 
  contacts: ImportContact[]
) {
  const result = await fetchJSON<{ data: CampaignTarget[]; count: number }>(
    buildApiUrl(`/api/telemarketing/campaigns/${campaignId}/targets`),
    { method: 'POST', body: { contacts, organization_id: organizationId } }
  );
  return result;
}

export async function importCSVToCampaign(
  campaignId: string,
  organizationId: string,
  csvContent: string,
  skipDuplicates: boolean = true
) {
  const result = await fetchJSON<{ data: CSVImportResult }>(
    buildApiUrl(`/api/telemarketing/campaigns/${campaignId}/import-csv`),
    { 
      method: 'POST', 
      body: { 
        csv_content: csvContent, 
        organization_id: organizationId,
        skip_duplicates: skipDuplicates
      } 
    }
  );
  return result.data;
}

export async function validateCSV(organizationId: string, csvContent: string) {
  const result = await fetchJSON<{ data: CSVImportResult }>(
    buildApiUrl('/api/telemarketing/campaigns/validate-csv'),
    { method: 'POST', body: { csv_content: csvContent, organization_id: organizationId } }
  );
  return result.data;
}

export async function deleteTarget(targetId: string) {
  return await fetchJSON(
    buildApiUrl(`/api/telemarketing/targets/${targetId}`),
    { method: 'DELETE' }
  );
}

// Alias for deleteTarget
export const removeTarget = deleteTarget;

export function getCSVTemplateUrl(): string {
  return buildApiUrl('/api/telemarketing/csv-template');
}
